import { useEffect, useMemo, useState } from 'react'
import { Grid, RadioGroup, FormControlLabel, Radio, Typography, Box, Link, Tabs, Tab } from '@mui/material'
import { BaseGridLayout, ICONE, formattaData, capitalizza, BaseTesto, BaseBarraRicercaConRitardo, contieneTutteLeParole } from '@lucan/base-ui'
import { useVerbali } from 'configuration/ConfigurazioneApi'
import { useVerbaliContext } from 'context/VerbaliProvider'
import ConfigManager from 'components/config/ConfigManager'
import { ELENCHI } from 'configuration/ConfigurazioneElenchi'
import Testo from 'components/ui/Testo'
import ScatolaRisultatoRicerca from 'components/ui/ScatolaRisultatoRicerca'
import ChiaveValore from 'components/ui/ChiaveValore'
import { calcolaStatoPagamentoVerbale, SERVIZIO_FRONTOFFICE, STATO_PAGAMENTO_VERBALE } from '../utilFrontoffice'
import Sezione from 'components/ui/Sezione'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import TitoloTab from 'components/ui/TitoloTab'
import { FRONTOFFICE_CONDIVISO, FRONTOFFICE_ELENCO_VERBALI } from 'configuration/frontoffice'
import LoadingPratiche from 'components/ui/LoadingPratiche'
import BadgeStatoPagamento from 'components/ui/frontoffice/BadgeStatoPagamento'
import useSchermoStretto from 'hook/useSchermoStretto'
import Titoletto from 'components/ui/Titoletto'

const CFG = FRONTOFFICE_ELENCO_VERBALI
const CFG_C = FRONTOFFICE_CONDIVISO

export default function ListaVerbaliWizard(props) {
  const { vaiAlVerbale, chiave, preavvisoRef } = props

  const {
    ScatolaMessaggi,
    getVerbali,
    inAttesaDelServer,
  } = useVerbali({ servizio: SERVIZIO_FRONTOFFICE })

  const { salvaVerbale, listaVerbali, setListaVerbali, showPreavviso } = useVerbaliContext()

  useEffect(() => {
    async function caricaVerbali() {
      const { ok, data } = await getVerbali()
      if (ok) {
        const { lista, messaggio } = data
        if (lista && !messaggio) {
          setListaVerbali(data.lista.reverse())
        }
      }
    }

    if (!listaVerbali) {
      caricaVerbali()
    }
  }, [])

  const verbaleScelto = (chiave) => {
    if (listaVerbali) {
      const findit = listaVerbali.find(({ chiave: c }) => chiave == c)
      if (findit) {
        salvaVerbale(findit.verbale)
      }
    }
    vaiAlVerbale(chiave)
  }

  function mostraPreavviso() {
    const element = preavvisoRef.current
    if (element) element.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <>
      {inAttesaDelServer ? (
        <LoadingPratiche />
      ) : (
        <ConfigManager
          elenchiConfig={[ELENCHI.CFG_FRONTOFFICE_ELENCO_VERBALI, ELENCHI.CFG_FRONTOFFICE_CONDIVISO]}
          render={propsConfig =>
            <>
              <Titoletto>{propsConfig?.txt(CFG.TITOLO)}</Titoletto>
              <Testo sx={{ mb: 3 }}>{propsConfig?.txt(CFG.SOTTOTITOLO)}</Testo>

              {showPreavviso && propsConfig?.val(CFG_C.PREAVVISO) &&
                <>
                  <Typography sx={{ mt: 1 }}>
                    Hai un <b>codice avviso? <Link sx={{ cursor: 'pointer' }} onClick={mostraPreavviso}>Inseriscilo qui</Link></b>
                  </Typography>
                  <br /><br />
                </>
              }

              {ScatolaMessaggi}

              {listaVerbali &&
                <ListaVerbaliConRicercaWizard
                  listaVerbali={listaVerbali}
                  vaiAlVerbale={verbaleScelto}
                  chiave={chiave}
                  propsConfig={propsConfig}
                />
              }
              <br /><br />
            </>
          }
        />
      )}
    </>
  )
}



function ListaVerbaliConRicercaWizard(props) {
  const { listaVerbali, vaiAlVerbale, propsConfig, chiave } = props
  const { val, txt } = propsConfig

  const [ricerca, setRicerca] = useState('')
  const [ricercaStato, setRicercaStato] = useState(null)

  const counterVerbali = useMemo(() => {
    return listaVerbali.reduce(
      (acc, v) => {
        const stato = calcolaStatoPagamentoVerbale(v?.verbale)

        acc.tot += 1

        if (stato === STATO_PAGAMENTO_VERBALE.PAGABILE) {
          acc.pagabili += 1
        } else if (stato === STATO_PAGAMENTO_VERBALE.PAGATO) {
          acc.pagati += 1
        }

        return acc
      },
      { tot: 0, pagabili: 0, pagati: 0 }
    )
  }, [listaVerbali])

  const listaVerbaliFiltrata = useMemo(() => {
    return listaVerbali.filter(({ verbale }) => {
      if (ricercaStato) {
        const stato = calcolaStatoPagamentoVerbale(verbale)
        if (stato != ricercaStato) {
          return false
        }
      }

      if (!ricerca) return true

      const {
        numero_verbale,
        infrazione_data,
        contestazione_data,
        procedura,
      } = verbale

      const infoConcatenate = [
        procedura,
        numero_verbale,
        formattaData(infrazione_data),
        formattaData(contestazione_data)
      ].join(' ')

      return contieneTutteLeParole(infoConcatenate, ricerca)
    })
  }, [listaVerbali, ricerca, ricercaStato])

  const schermoStretto = useSchermoStretto()

  const tabs = [
    {
      value: null,
      icona: ICONE.DESCRIZIONE,
      titolo: `Totale: ${counterVerbali.tot}`,
    },
    {
      value: STATO_PAGAMENTO_VERBALE.PAGABILE,
      icona: CreditCardIcon,
      titolo: `Pagabili: ${counterVerbali.pagabili}`,
    },
    {
      value: STATO_PAGAMENTO_VERBALE.PAGATO,
      icona: ICONE.CONFERMA,
      titolo: `Pagati: ${counterVerbali.pagati}`,
    },
  ]

  const handleChange = (event, newValue) => {
    setRicercaStato(newValue)
  }
  
  return (
    <>
      {listaVerbali.length > 0 &&
        <BaseGridLayout vertical noWrap spacing={3} >
          <Sezione>
            <Box>
              <BaseBarraRicercaConRitardo
                label={val(CFG.PLACEHOLDER_RICERCA)}
                setTestoRicerca_FinitoDiScrivere={setRicerca}
                messaggioAiuto=''
                numeroMinimoCaratteriRicerca={1}
                larghezza={schermoStretto ? 'fullWidth' : 30}
              />
              {!val(CFG_C.SOLOPAGABILI) &&
                <Tabs
                  variant="fullWidth"
                  value={ricercaStato}
                  onChange={handleChange}
                  indicatorColor='primary'
                >
                  {tabs.map(({ titolo, icona, value }, indice) =>
                    <Tab
                      label={<TitoloTab Icona={icona} titolo={titolo} />}
                      value={value}
                      key={indice}
                    />
                  )}
                </Tabs>
              }
            </Box>

            <RadioGroup
              value={chiave}
              sx={{ flexWrap: ' noWrap', maxHeight: '40vh', overflow: 'auto' }}
              onChange={e => vaiAlVerbale(e.target.value)}
            >
              <Box>
                {listaVerbaliFiltrata.map(verbale => (
                  <Box
                    key={verbale.chiave}
                    className={'listaVerbaliFiltrata'}
                    sx={{ width: '100%', bgcolor: '#fff', m: 0, p: 2, marginBottom: '1px' }}
                  >
                    <FormControlLabel
                      slotProps={{
                        typography: {
                          sx: { width: '100%' }, // stile della label
                        },
                      }}
                      sx={{ width: '100%' }}
                      value={verbale.chiave}
                      control={<Radio />}
                      label={
                        <ScatolaVerbale
                          verbale={verbale}
                          key={verbale.chiave}
                          {...propsConfig}
                        />
                      }
                    />
                  </Box>
                ))}
              </Box>
            </RadioGroup>
          </Sezione>
        </BaseGridLayout>
      }

      {listaVerbaliFiltrata.length === 0 &&
        <BaseTesto>{txt(CFG.NESSUN_VERBALE_TROVATO)}</BaseTesto>
      }
    </>
  )
}



function ScatolaVerbale(props) {
  const { verbale, txt } = props
  const {
    verbale: {
      numero_verbale,
      infrazione_data,
      contestazione_data,
      procedura,
    }
  } = verbale

  return (
    <ScatolaRisultatoRicerca
      className="risultato"
      Icona={ICONE.DESCRIZIONE}
      titolo={`${capitalizza(procedura)} ${numero_verbale}`}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} lg={4}>
          <ChiaveValore
            chiave={txt(CFG.DATA_INFRAZIONE)}
            valore={formattaData(infrazione_data)}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={4}>
          <ChiaveValore
            chiave={txt(CFG.DATA_CONTESTAZIONE)}
            valore={formattaData(contestazione_data)}
          />
        </Grid>

        <Grid item xs={12} sm={12} lg={4}>
          <BadgeStatoPagamento verbale={verbale.verbale} />
        </Grid>
      </Grid>
    </ScatolaRisultatoRicerca>
  )
}